import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import './Contact.css'; // Retain the old CSS for styling
import BannerUpdate from '../../components/BannerUpdate/BannerUpadate';
import Swal from 'sweetalert2'; // SweetAlert2 for alerts
import * as XLSX from 'xlsx';
import API_URL from '../../api/ExamCalendarApi';

const Contact = () => {
  const [entries, setEntries] = useState([]);
  const [courses, setCourses] = useState([]); // Courses state
  const [courseName, setCourseName] = useState(""); // Input for course name
  const [loading, setLoading] = useState(true); // Loading states
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [editingCourseId, setEditingCourseId] = useState(null); // ID of the course being edited
  const [monthFilter, setMonthFilter] = useState(""); // Month filter
  const [yearFilter, setYearFilter] = useState(""); // Year filter

  // Fetch all entries on component mount
  useEffect(() => {
    axios.get(`${API_URL}/contact/get-entries`)
      .then((response) => {
        const sortedEntries = response.data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setEntries(sortedEntries);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching contact form entries:', error);
        setLoading(false);
      });

    // Fetch courses
    axios.get(`${API_URL}/contact/get-interested-courses`)
      .then((response) => {
        setCourses(response.data);
        setLoadingCourses(false);
      })
      .catch((error) => {
        console.error('Error fetching courses:', error);
        setLoadingCourses(false);
      });
  }, [API_URL]);

  // Generate month and year filters from data
  const months = useMemo(() => {
    const monthSet = new Set();
    entries.forEach(entry => {
      const date = new Date(entry.timestamp);
      monthSet.add(date.toLocaleString('default', { month: 'long' }));
    });
    return Array.from(monthSet);
  }, [entries]);

  const years = useMemo(() => {
    const yearSet = new Set();
    entries.forEach(entry => {
      const date = new Date(entry.timestamp);
      yearSet.add(date.getFullYear());
    });
    return Array.from(yearSet);
  }, [entries]);

  // Filtered entries by selected month and year
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      const date = new Date(entry.timestamp);
      const monthMatch = monthFilter ? date.toLocaleString('default', { month: 'long' }) === monthFilter : true;
      const yearMatch = yearFilter ? date.getFullYear() === parseInt(yearFilter) : true;
      return monthMatch && yearMatch;
    });
  }, [entries, monthFilter, yearFilter]);

  // Download filtered data as Excel
  const handleDownloadExcel = () => {
    const dataToExport = monthFilter || yearFilter ? filteredEntries : entries;
    const data = dataToExport.map((entry) => ({
      "Name": entry.name,
      "Mobile Number": entry.mobile,
      "Email ID": entry.email,
      "Interested Course": entry.course,
      "City": entry.city,
      "Message": entry.message,
      "Submission Time": new Date(entry.timestamp).toLocaleString(),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Contact Entries");
    XLSX.writeFile(wb, 'contact_entries.xlsx');
  };

  // Handle Add Course functionality
  const handleAddCourse = () => {
    if (courseName.trim()) {
      axios.post(`${API_URL}/contact/add-interested-course`, { courseName })
        .then(() => {
          Swal.fire('Course Added!', 'The course has been added successfully.', 'success');
          setCourses(prevCourses => [...prevCourses, { courseName }]);
          setCourseName(""); // Clear input
        })
        .catch((error) => {
          console.error('Error adding course:', error);
          Swal.fire('Error!', 'There was an error adding the course.', 'error');
        });
    }
  };

  // Edit course mode
  const handleEditCourse = (courseId, courseName) => {
    setEditingCourseId(courseId);
    setCourseName(courseName); // Set course name in input
  };

  // Update course logic
  const handleUpdateCourse = (courseId) => {
    if (courseName.trim()) {
      axios.put(`${API_URL}/contact/update-interested-course/${courseId}`, { courseName })
        .then(() => {
          Swal.fire('Course Updated!', 'The course has been updated successfully.', 'success');
          setCourses(prevCourses => prevCourses.map(course =>
            course._id === courseId ? { ...course, courseName } : course
          ));
          setCourseName(""); // Clear input
          setEditingCourseId(null); // Reset editing
        })
        .catch((error) => {
          console.error('Error updating course:', error);
          Swal.fire('Error!', 'There was an error updating the course.', 'error');
        });
    }
  };

  // Delete course logic
  const handleDeleteCourse = (courseId) => {
    if (!courseId) {
      console.error("Course ID is undefined");
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to delete this course!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        axios.delete(`${API_URL}/contact/delete-interested-course/${courseId}`)
          .then(() => {
            Swal.fire('Course Deleted!', 'The course has been deleted successfully.', 'success');
            setCourses(prevCourses => prevCourses.filter(course => course._id !== courseId));
          })
          .catch((error) => {
            console.error('Error deleting course:', error);
            Swal.fire('Error!', 'There was an error deleting the course.', 'error');
          });
      }
    });
  };

  const handleDeleteEntry = async (mobile, email) => {
    if (!mobile && !email) {
      console.error('Mobile or email must be provided to delete the entry');
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Send either mobile or email
          await axios.delete(`${API_URL}/contact/delete-entry`, {
            data: { mobile, email },
          });
          Swal.fire('Deleted!', 'The entry has been deleted.', 'success');

          // Re-fetch or update entries
          const updatedEntries = await axios.get(`${API_URL}/contact/get-entries`);
          const sortedEntries = updatedEntries.data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setEntries(sortedEntries); // Update state with the latest entries
        } catch (error) {
          console.error('Error deleting form entry:', error);
          Swal.fire('Error!', 'There was an error deleting the entry.', 'error');
        }
      }
    });
  };



  return (
    <div className="admin-contact-container">
      <div className="admin-contact-section">
        <BannerUpdate pageName="contact" />
        <h2 className="admin-contact-title mb-4 text-center">Contact Entries</h2>

        {/* Month and Year Filters */}
        <div className="filter-dropdowns">
          <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="filter-dropdown">
            <option value="">Select Month</option>
            {months.map((month) => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="filter-dropdown">
            <option value="">Select Year</option>
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="admin-contact-loading text-center">Loading entries...</p>
        ) : (
          <>
            <div className="admin-contact-table-wrapper">
              <table className="admin-contact-table">
                <thead className="admin-contact-thead">
                  <tr>
                    <th>Name</th>
                    <th>Mobile Number</th>
                    <th>Email ID</th>
                    <th>Interested Course</th>
                    <th>City</th>
                    <th>Message</th>
                    <th>Submission Time</th>
                    <th>Action</th> {/* Action column for delete */}
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, index) => (
                    <tr key={entry._id}>
                      <td>{entry.name}</td>
                      <td>{entry.mobile}</td>
                      <td>{entry.email}</td>
                      <td>{entry.course}</td>
                      <td>{entry.city}</td>
                      <td>{entry.message}</td>
                      <td>{new Date(entry.timestamp).toLocaleString()}</td>
                      <td>
                       
                          <button onClick={() => handleDeleteEntry(entry.mobile, entry.email)}>Delete</button>
                        
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Excel Download Button */}
            <div className="admin-contact-actions">
              <button onClick={handleDownloadExcel} className="download-excel-btn">Download Excel</button>
            </div>
          </>
        )}

        {/* Course Management Section */}
        <h2 className="admin-contact-title text-center">Manage Interested Courses</h2>
        <div className="admin-contact-courses">
          <input
            type="text"
            value={courseName}
            onChange={(e) => setCourseName(e.target.value)}
            placeholder="Enter Course Name"
            className="admin-contact-course-input"
          />
          {editingCourseId ? (
            <>
              <button onClick={() => handleUpdateCourse(editingCourseId)} className="admin-contact-course-btn">Update Course</button>
              <button onClick={() => { setEditingCourseId(null); setCourseName(''); }} className="admin-contact-course-btn cancel-btn">Cancel</button>
            </>
          ) : (
            <button onClick={handleAddCourse} className="admin-contact-course-btn">Add Course</button>
          )}
        </div>

        {loadingCourses ? (
          <p className="admin-contact-loading text-center">Loading courses...</p>
        ) : (
          <div className="admin-contact-courses-list" id="admin-contact-courses-list">
            {courses.map((course) => (
              <div key={course._id} className="admin-contact-course-item">
                <span>{course.courseName}</span>
                <div className='course-buttons'>
                <button onClick={() => handleEditCourse(course._id, course.courseName)}>Edit</button>
                <button onClick={() => handleDeleteCourse(course._id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Contact;
